import React, { useState, useMemo, useRef } from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { Card, Typography, Switch, Space, Tooltip, Button, Table, Tag } from 'antd';
import { InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';

const { Title, Text, Paragraph } = Typography;

// 定义预测数据接口
interface EpitopePredictionData {
  // CLBTope格式
  Seq_ID?: string;
  Pattern_ID?: string;
  Sequence?: string;
  ML_Score?: number;
  BLAST_Score?: number;
  Total_Score?: number;
  Prediction?: string;
  // 兼容旧BepiPred3格式
  Accession?: string;
  Residue?: string;
  "BepiPred-3.0 score"?: number;
  "BepiPred-3.0 linear epitope score"?: number;
  "BepiPred-3.0 rolling mean score"?: number;
  position?: number;
}

// 表位区间接口
interface EpitopeRegion {
  key: string;
  start: number;
  end: number;
  length: number;
  sequence: string;
  avgScore: number;
  maxScore: number;
  positions: number[];
}

// 表位预测结果组件
const EpitopePredictionResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults
}) => {
  const [useRollingMean, setUseRollingMean] = useState(true); // 改为默认true
  const [threshold, setThreshold] = useState(0.5);
  const chartRef = useRef<any>(null);

  // 解析预测数据
  const predictionData: EpitopePredictionData[] = useMemo(() => {
    // 处理统一格式数据：从results[0].result中提取实际数据
    let epitopeData = data;
    if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
      epitopeData = data.results[0].result;
    }
    
    if (!epitopeData || !epitopeData.raw_predictions) {
      return [];
    }
    
    // 检测数据格式（CLBTope vs BepiPred3）
    const rawPredictions = epitopeData.raw_predictions;
    if (rawPredictions.length > 0 && rawPredictions[0].Seq_ID !== undefined) {
      // CLBTope 格式 - 每行是一个肽段
      return rawPredictions.map((item: any, index: number) => ({
        ...item,
        position: index + 1
      }));
    } else {
      // BepiPred3 格式 - 逐残基
      return rawPredictions.map((item: any, index: number) => ({
        ...item,
        position: index + 1
      }));
    }
  }, [data]);

  // 准备图表数据
  const chartData = useMemo(() => {
    if (predictionData.length === 0) return null;

    const scoreKey = useRollingMean ? "BepiPred-3.0 linear epitope score" : "BepiPred-3.0 score";
    
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const dataIndex = params[0].dataIndex;
          const item = predictionData[dataIndex];
          const score = item[scoreKey];
          return `
            <div>
              <strong>位置:</strong> ${item.position}<br/>
              <strong>残基:</strong> ${item.Residue}<br/>
              <strong>得分:</strong> ${typeof score === 'number' ? score.toFixed(4) : 'N/A'}<br/>
              <strong>蛋白质:</strong> ${item.Accession}
            </div>
          `;
        }
      },
      xAxis: {
        type: 'category',
        data: predictionData.map(item => item.position),
        name: '氨基酸位置',
        nameLocation: 'middle',
        nameGap: 30
      },
      yAxis: {
        type: 'value',
        name: useRollingMean ? 'Rolling Mean Score' : 'BepiPred-3.0 Score',
        nameLocation: 'middle',
        nameGap: 50,
        min: 0,
        max: 1
      },
      series: [
        {
          name: useRollingMean ? 'Rolling Mean Score' : 'BepiPred-3.0 Score',
          type: 'line',
          data: predictionData.map(item => item[scoreKey]),
          lineStyle: {
            width: 2,
            color: useRollingMean ? '#52c41a' : '#1890ff'
          },
          itemStyle: {
            color: useRollingMean ? '#52c41a' : '#1890ff'
          },
          symbol: 'circle',
          symbolSize: 4,
          markLine: {
            data: [
              {
                yAxis: threshold,
                lineStyle: {
                  color: '#ff4d4f',
                  type: 'dashed',
                  width: 2
                },
                label: {
                  formatter: `阈值: ${threshold.toFixed(2)}`,
                  position: 'end'
                }
              }
            ],
            // 添加拖拽功能
            silent: false,
            animation: false
          }
        }
      ],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '10%'
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100
        }
      ],
      // 添加图表事件处理
      graphic: [
        {
          type: 'line',
          shape: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 0
          },
          style: {
            stroke: 'transparent'
          },
          silent: true
        }
      ]
    };
  }, [predictionData, useRollingMean, threshold]);

  // 处理图表事件
  const onChartReady = (chartInstance: any) => {
    chartRef.current = chartInstance;
    
    // 监听鼠标事件来实现阈值线拖拽
    chartInstance.getZr().on('mousedown', (e: any) => {
      const pointInPixel = [e.offsetX, e.offsetY];
      const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
      
      if (pointInGrid && pointInGrid[1] !== null) {
        const yValue = pointInGrid[1];
        // 检查是否点击在阈值线附近（容差范围内）
        if (Math.abs(yValue - threshold) < 0.05) {
          let isDragging = true;
          
          const onMouseMove = (moveEvent: any) => {
            if (!isDragging) return;
            
            const movePointInPixel = [moveEvent.offsetX, moveEvent.offsetY];
            const movePointInGrid = chartInstance.convertFromPixel('grid', movePointInPixel);
            
            if (movePointInGrid && movePointInGrid[1] !== null) {
              const newThreshold = Math.max(0, Math.min(1, movePointInGrid[1]));
              setThreshold(newThreshold);
            }
          };
          
          const onMouseUp = () => {
            isDragging = false;
            chartInstance.getZr().off('mousemove', onMouseMove);
            chartInstance.getZr().off('mouseup', onMouseUp);
          };
          
          chartInstance.getZr().on('mousemove', onMouseMove);
          chartInstance.getZr().on('mouseup', onMouseUp);
        }
      }
    });
    
    // 添加鼠标悬停效果
    chartInstance.getZr().on('mousemove', (e: any) => {
      const pointInPixel = [e.offsetX, e.offsetY];
      const pointInGrid = chartInstance.convertFromPixel('grid', pointInPixel);
      
      if (pointInGrid && pointInGrid[1] !== null) {
        const yValue = pointInGrid[1];
        // 检查是否悬停在阈值线附近
        if (Math.abs(yValue - threshold) < 0.05) {
          chartInstance.getZr().setCursorStyle('ns-resize');
        } else {
          chartInstance.getZr().setCursorStyle('default');
        }
      }
    });
  };

  // 筛选表位数据（只显示超过阈值的）
  const epitopeData = useMemo(() => {
    const scoreKey = useRollingMean ? "BepiPred-3.0 linear epitope score" : "BepiPred-3.0 score";
    
    return predictionData
      .filter(item => {
        const score = item[scoreKey];
        return typeof score === 'number' && score >= threshold;
      })
      .map((item, index) => ({
        key: index,
        position: item.position,
        residue: item.Residue,
        score: item[scoreKey] || 0,
        rawScore: item["BepiPred-3.0 score"] || 0,
        rollingScore: item["BepiPred-3.0 linear epitope score"] || item["BepiPred-3.0 rolling mean score"] || 0,
        accession: item.Accession
      }));
  }, [predictionData, useRollingMean, threshold]);

  // 分析连续表位区间
  const epitopeRegions = useMemo(() => {
    if (epitopeData.length === 0) return [];
    
    const regions: EpitopeRegion[] = [];
    let currentRegion: number[] = [];
    
    // 按位置排序
    const sortedEpitopes = [...epitopeData].sort((a, b) => (a?.position || 0) - (b?.position || 0));
    
    for (let i = 0; i < sortedEpitopes.length; i++) {
      const current = sortedEpitopes[i];
      const next = sortedEpitopes[i + 1];
      
      if (current.position !== undefined) {
        currentRegion.push(current.position);
      }
      
      // 如果下一个位置不连续或者是最后一个，结束当前区间
      if (!next || (current.position !== undefined && next.position !== current.position + 1)) {
        // 构建区间序列
        const sequence = currentRegion
          .map(pos => predictionData.find(item => item.position === pos)?.Residue || '')
          .join('');
        
        // 计算平均分数和最高分数
        const scores = currentRegion.map(pos => {
          const item = predictionData.find(item => item.position === pos);
          const scoreKey = useRollingMean ? "BepiPred-3.0 linear epitope score" : "BepiPred-3.0 score";
          return item && typeof item[scoreKey] === 'number' ? item[scoreKey] : 0;
        });
        
        const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        const maxScore = Math.max(...scores);
        
        regions.push({
          key: `region-${regions.length}`,
          start: Math.min(...currentRegion),
          end: Math.max(...currentRegion),
          length: currentRegion.length,
          sequence,
          avgScore,
          maxScore,
          positions: [...currentRegion]
        });
        
        currentRegion = [];
      }
    }
    
    return regions;
  }, [epitopeData, predictionData, useRollingMean]);

  // 表位详细数据表格列定义
  const epitopeColumns = [
    {
      title: '位置',
      dataIndex: 'position',
      key: 'position',
      width: 80,
      sorter: (a: any, b: any) => a.position - b.position
    },
    {
      title: '残基',
      dataIndex: 'residue',
      key: 'residue',
      width: 80,
      render: (residue: string) => (
        <Text style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
          {residue}
        </Text>
      )
    },
    {
      title: 'BepiPred-3.0 Score',
      dataIndex: 'rawScore',
      key: 'rawScore',
      width: 150,
      render: (score: number) => typeof score === 'number' ? score.toFixed(4) : 'N/A',
      sorter: (a: any, b: any) => (a.rawScore || 0) - (b.rawScore || 0)
    },
    {
      title: 'Linear Epitope Score',
      dataIndex: 'rollingScore',
      key: 'rollingScore',
      width: 150,
      render: (score: number) => typeof score === 'number' ? score.toFixed(4) : 'N/A',
      sorter: (a: any, b: any) => (a.rollingScore || 0) - (b.rollingScore || 0)
    },
    {
      title: '当前得分',
      dataIndex: 'score',
      key: 'currentScore',
      width: 120,
      render: (score: number) => (
        <Text strong style={{ color: useRollingMean ? '#52c41a' : '#1890ff' }}>
          {typeof score === 'number' ? score.toFixed(4) : 'N/A'}
        </Text>
      ),
      sorter: (a: any, b: any) => (a.score || 0) - (b.score || 0)
    }
  ];

  // 表位区间表格列定义
  const regionColumns = [
    {
      title: '区间',
      key: 'region',
      width: 120,
      render: (_: any, record: EpitopeRegion) => (
        <Text strong>
          {record.start === record.end ? 
            `${record.start}` : 
            `${record.start}-${record.end}`
          }
        </Text>
      )
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
      width: 80,
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => a.length - b.length
    },
    {
      title: '表位序列',
      dataIndex: 'sequence',
      key: 'sequence',
      render: (sequence: string) => (
        <Text 
          style={{ 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            color: '#ff4d4f',
            backgroundColor: '#fff2f0',
            padding: '2px 4px',
            borderRadius: '2px'
          }}
        >
          {sequence}
        </Text>
      )
    },
    {
      title: '平均得分',
      dataIndex: 'avgScore',
      key: 'avgScore',
      width: 120,
      render: (score: number) => typeof score === 'number' ? score.toFixed(4) : 'N/A',
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => (a.avgScore || 0) - (b.avgScore || 0)
    },
    {
      title: '最高得分',
      dataIndex: 'maxScore',
      key: 'maxScore',
      width: 120,
      render: (score: number) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          {typeof score === 'number' ? score.toFixed(4) : 'N/A'}
        </Text>
      ),
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => (a.maxScore || 0) - (b.maxScore || 0)
    },
    {
      title: '位置详情',
      dataIndex: 'positions',
      key: 'positions',
      render: (positions: number[]) => (
        <Text style={{ fontSize: '12px', color: '#666' }}>
          {positions.join(', ')}
        </Text>
      )
    }
  ];

  // 统计信息
  const stats = useMemo(() => {
    const totalCount = predictionData.length;
    const epitopeCount = epitopeData.length;
    const regionCount = epitopeRegions.length;
    const percentage = totalCount > 0 ? (epitopeCount / totalCount * 100).toFixed(1) : '0';
    
    return {
      total: totalCount,
      epitope: epitopeCount,
      regions: regionCount,
      percentage
    };
  }, [predictionData, epitopeData, epitopeRegions]);

  // 导出数据
  const exportData = () => {
    const csvContent = [
      // 表位区间数据
      '=== 表位区间 ===',
      ['区间', '长度', '表位序列', '平均得分', '最高得分', '位置详情'].join(','),
      ...epitopeRegions.map(region => [
        region.start === region.end ? region.start : `${region.start}-${region.end}`,
        region.length,
        region.sequence,
        typeof region.avgScore === 'number' ? region.avgScore.toFixed(4) : 'N/A',
        typeof region.maxScore === 'number' ? region.maxScore.toFixed(4) : 'N/A',
        region.positions.join(';')
      ].join(',')),
      '',
      '=== 表位详细数据 ===',
      ['位置', '残基', 'BepiPred-3.0 Score', 'Rolling Mean Score', '当前得分'].join(','),
      ...epitopeData.map(item => [
        item.position,
        item.residue,
        item.rawScore.toFixed(4),
        item.rollingScore.toFixed(4),
        item.score.toFixed(4)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `epitope_prediction_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 检查数据是否有效
  const hasValidData = useMemo(() => {
    let epitopeData = data;
    if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
      epitopeData = data.results[0].result;
    }
    return epitopeData && epitopeData.raw_predictions && epitopeData.raw_predictions.length > 0;
  }, [data]);

  if (!hasValidData) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "抗原表位预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
      >
        <Text type="warning">无法显示表位预测结果，数据格式不正确或为空。</Text>
      </BaseResultDisplay>
    );
  }

  return (
    <BaseResultDisplay
      data={data}
      title={title || "抗原表位预测结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 控制面板 */}
        <Card size="small">
          <Space align="center" wrap>
            <Text strong>显示模式:</Text>
            <Switch
              checked={useRollingMean}
              onChange={setUseRollingMean}
              checkedChildren="Rolling Mean"
              unCheckedChildren="Raw Score"
            />
            <Tooltip 
              title={
                <div>
                  <div><strong>Raw Score:</strong> 原始BepiPred-3.0预测得分，反映每个氨基酸位点的表位可能性</div>
                  <div style={{ marginTop: 8 }}><strong>Rolling Mean Score:</strong> 滑动窗口平均得分，通过平滑处理减少噪音，更好地识别连续的表位区域</div>
                </div>
              }
              placement="right"
            >
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
            </Tooltip>
            
            <Text strong style={{ marginLeft: 24 }}>阈值:</Text>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              style={{ width: 100 }}
            />
            <Text>{threshold.toFixed(2)}</Text>
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
              (可在图表中拖拽红线调节)
            </Text>
            
            <Button 
              icon={<DownloadOutlined />} 
              onClick={exportData}
              style={{ marginLeft: 24 }}
            >
              导出CSV
            </Button>
          </Space>
        </Card>

        {/* 统计信息 */}
        <Card size="small">
          <Space>
            <Text>总残基数: <Text strong>{stats.total}</Text></Text>
            <Text>表位残基数: <Text strong style={{ color: '#ff4d4f' }}>{stats.epitope}</Text></Text>
            <Text>表位区间数: <Text strong style={{ color: '#ff4d4f' }}>{stats.regions}</Text></Text>
            <Text>表位比例: <Text strong>{stats.percentage}%</Text></Text>
            <Text>当前模式: <Text strong style={{ color: useRollingMean ? '#52c41a' : '#1890ff' }}>
              {useRollingMean ? 'Rolling Mean Score' : 'Raw BepiPred-3.0 Score'}
            </Text></Text>
          </Space>
        </Card>

        {/* 图表可视化 */}
        <Card title="表位预测可视化" size="small">
          {chartData && (
            <ReactECharts
              ref={chartRef}
              option={chartData}
              style={{ height: '400px', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              onChartReady={onChartReady}
            />
          )}
        </Card>

        {/* 表位区间汇总 */}
        <Card title={`表位区间汇总 (共${stats.regions}个区间)`} size="small">
          <Table
            columns={regionColumns}
            dataSource={epitopeRegions}
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
          />
        </Card>

        {/* 表位详细数据 */}
        <Card title={`表位详细数据 (共${stats.epitope}个残基)`} size="small">
          <Table
            columns={epitopeColumns}
            dataSource={epitopeData}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }}
            size="small"
            scroll={{ x: 800 }}
          />
        </Card>
      </Space>
    </BaseResultDisplay>
  );
};

export default EpitopePredictionResult;